// Per-route polyline projection helpers used by the Stringline diagram.
//
// We pick the longest polyline as the route's "canonical" line, walk it
// once to precompute cumulative segment distances in meters, and then
// expose `project(lat, lon)` that returns the meters-from-start of the
// nearest point on the line.
//
// Distances use a local equirectangular approximation: at the latitudes
// we care about (Puget Sound) this is accurate to a fraction of a percent
// over the few-km segment lengths involved, and it's a couple of orders
// of magnitude cheaper than full Haversine.

export type LatLng = { lat: number; lon: number };

export type RawRouteGeometry = {
  route_id: string;
  route_short_name: string;
  route_long_name: string;
  /** Hex without leading '#'. */
  route_color: string;
  polylines: LatLng[][];
  stops: { id: string; name: string; code: string; lat: number; lon: number }[];
};

export type ProjectedStop = {
  id: string;
  name: string;
  code: string;
  /** Meters along canonical polyline. */
  distM: number;
};

export type RouteProjection = {
  routeId: string;
  shortName: string;
  longName: string;
  /** CSS hex color (with leading '#'). */
  color: string;
  /** Total length of the canonical polyline in meters. */
  lengthM: number;
  /** Stops snapped to the polyline, sorted by distance from start. */
  stops: ProjectedStop[];
  /**
   * Snap an arbitrary lat/lon to the canonical polyline and return its
   * meters-from-start. Returns null if the snap is too far away (likely
   * a stale or off-route position) — caller should drop the sample.
   */
  project: (lat: number, lon: number) => number | null;
};

const EARTH_R = 6371000; // meters
const DEG2RAD = Math.PI / 180;

// Maximum perpendicular distance from the polyline a sample is allowed to
// be before we consider it "off route" and drop it. Well above realistic
// GPS error and the slack we'd expect from track curvature vs. polyline
// resolution.
const MAX_SNAP_M = 800;

export function buildRouteProjection(g: RawRouteGeometry): RouteProjection {
  // Pick the longest polyline as canonical. Most rail routes ship one
  // polyline per direction; geographically they overlap, so picking the
  // longer one (which usually traverses every stop) is good enough.
  let canonical: LatLng[] = [];
  for (const p of g.polylines) {
    if (p.length > canonical.length) canonical = p;
  }

  // Cache the meters-per-degree conversion at the polyline's mean latitude
  // so the equirectangular flattening is locally faithful.
  const meanLat =
    canonical.reduce((s, p) => s + p.lat, 0) / Math.max(canonical.length, 1);
  const cosLat = Math.cos(meanLat * DEG2RAD);
  const mPerDegLat = (Math.PI * EARTH_R) / 180;
  const mPerDegLon = mPerDegLat * cosLat;

  // Project each vertex into a local meters frame anchored at canonical[0].
  // From there it's trivial 2D math.
  const xs: number[] = new Array(canonical.length);
  const ys: number[] = new Array(canonical.length);
  if (canonical.length > 0) {
    const lat0 = canonical[0].lat;
    const lon0 = canonical[0].lon;
    for (let i = 0; i < canonical.length; i++) {
      xs[i] = (canonical[i].lon - lon0) * mPerDegLon;
      ys[i] = (canonical[i].lat - lat0) * mPerDegLat;
    }
  }
  const segDist: number[] = new Array(Math.max(canonical.length - 1, 0));
  const cumDist: number[] = new Array(canonical.length);
  cumDist[0] = 0;
  for (let i = 0; i < canonical.length - 1; i++) {
    const dx = xs[i + 1] - xs[i];
    const dy = ys[i + 1] - ys[i];
    const d = Math.hypot(dx, dy);
    segDist[i] = d;
    cumDist[i + 1] = cumDist[i] + d;
  }
  const lengthM = canonical.length > 0 ? cumDist[canonical.length - 1] : 0;

  function projectXY(px: number, py: number): { distM: number; perpM: number } {
    let bestDist = 0;
    let bestPerp = Infinity;
    for (let i = 0; i < canonical.length - 1; i++) {
      const ax = xs[i];
      const ay = ys[i];
      const bx = xs[i + 1];
      const by = ys[i + 1];
      const dx = bx - ax;
      const dy = by - ay;
      const len2 = dx * dx + dy * dy;
      let t = 0;
      if (len2 > 0) t = ((px - ax) * dx + (py - ay) * dy) / len2;
      if (t < 0) t = 0;
      else if (t > 1) t = 1;
      const cx = ax + t * dx;
      const cy = ay + t * dy;
      const perp = Math.hypot(px - cx, py - cy);
      if (perp < bestPerp) {
        bestPerp = perp;
        bestDist = cumDist[i] + t * segDist[i];
      }
    }
    return { distM: bestDist, perpM: bestPerp };
  }

  const lat0 = canonical[0]?.lat ?? 0;
  const lon0 = canonical[0]?.lon ?? 0;

  function project(lat: number, lon: number): number | null {
    if (canonical.length < 2) return null;
    const px = (lon - lon0) * mPerDegLon;
    const py = (lat - lat0) * mPerDegLat;
    const { distM, perpM } = projectXY(px, py);
    if (perpM > MAX_SNAP_M) return null;
    return distM;
  }

  // Snap stops to the canonical polyline. We deliberately keep stops that
  // snap further than MAX_SNAP_M (sometimes a route has a branch the
  // canonical polyline doesn't cover) but mark them with the closest
  // distance we found — the chart will still place them sensibly.
  const stops: ProjectedStop[] = g.stops
    .map((s) => {
      const px = (s.lon - lon0) * mPerDegLon;
      const py = (s.lat - lat0) * mPerDegLat;
      const { distM } = projectXY(px, py);
      return { id: s.id, name: s.name, code: s.code, distM };
    })
    .sort((a, b) => a.distM - b.distM);

  return {
    routeId: g.route_id,
    shortName: g.route_short_name,
    longName: g.route_long_name,
    color: g.route_color ? `#${g.route_color}` : "#1f78ff",
    lengthM,
    stops,
    project,
  };
}
